import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Quote } from 'lucide-react';
import { useFadeInObserver } from '@/hooks/use-fade-in-observer';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  country: string;
  flag: string;
  initials: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Rakib helped our website go from page 5 to page 1 on Google in just 3 months. Incredible results and very transparent reporting throughout the campaign.",
    name: 'James R.',
    role: 'E-commerce Owner',
    country: 'USA',
    flag: '🇺🇸',
    initials: 'JR',
  },
  {
    quote: "Highly professional and communicates very well. Our organic traffic doubled within the first quarter. Will definitely keep working with him.",
    name: 'Sophie L.',
    role: 'Marketing Manager',
    country: 'UK',
    flag: '🇬🇧',
    initials: 'SL',
  },
  {
    quote: "Best SEO expert I've ever worked with. Transparent reporting, real results, and deep technical knowledge. Highly recommended for any serious business.",
    name: 'Karim A.',
    role: 'Business Owner',
    country: 'UAE',
    flag: '🇦🇪',
    initials: 'KA',
  },
];

const TestimonialsSection = () => {
  const [active, setActive] = useState(0);
  const { ref, isVisible } = useFadeInObserver(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="testimonials" className="py-24 bg-secondary/30" aria-label="Client testimonials">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-primary font-semibold mb-2 uppercase tracking-wider text-sm">Testimonials</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">What Clients Say</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Real feedback from businesses that trusted me with their SEO and digital growth.
          </p>
        </div>

        <div
          ref={ref}
          className={`grid md:grid-cols-3 gap-6 max-w-6xl mx-auto transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {testimonials.map((t, i) => (
            <Card
              key={i}
              className={`relative border-border transition-all duration-500 hover:shadow-[var(--shadow-elegant)] hover:-translate-y-2 ${
                active === i ? 'ring-2 ring-primary/40 shadow-[var(--shadow-elegant)]' : ''
              }`}
              onMouseEnter={() => setActive(i)}
            >
              <CardContent className="p-6 flex flex-col h-full">
                <Quote className="w-8 h-8 text-primary/40 mb-4" aria-hidden="true" />
                <div className="flex gap-1 mb-4" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-primary text-primary" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-foreground/90 italic mb-6 flex-grow leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-glow text-primary-foreground flex items-center justify-center font-bold">
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-semibold">{t.name} <span className="ml-1">{t.flag}</span></p>
                    <p className="text-sm text-muted-foreground">{t.role}, {t.country}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
